<template>
  <div class="range">
    <div class="range__result-container" v-if="!!dateResult && !useFields">
      <div class="range__result">{{dateResult}}</div>
      <div class="range__clear-button" @click.stop="clear()"></div>
      <div class="range__separator"/>
    </div>
    <div class="range__date-picker">
      <v-date-picker
        ref="picker"
        id="range"
        mode="range"
        v-model="range"
        :select-attribute="selectDragAttribute"
        :drag-attribute="selectDragAttribute"
        @drag="dragValue = $event"
        :attributes="attributes"
        :min-date="datePickerMinDate"
        :max-date="datePickerMaxDate"
        :from-page="fromPage"
        :columns="columns"
        @dayclick="handleDayClick"
        value=""
        is-inline>
        <div slot="day-popover" slot-scope="{format}">
          {{ format(dragValue ? dragValue.start : range.start, 'MMM D') }}
          -
          {{ format(dragValue ? dragValue.end : range.end, 'MMM D') }}
        </div>
      </v-date-picker>
      <div class="range__picker-fields" @click.stop="()=>{}" v-if="useFields">
        <div class="range__picker-fields_row">
          <span style="font-weight: bold">Date Range:</span>
          <base-select
           :options="dateRangeOptions"
           alignLeft
           borderLess
           v-model="dateRange"
           :titleStyle="{paddingRight: '33px'}"
           :selectStyle="{height: '17px!important', marginLeft: '16px'}" />
        </div>
        <div class="range__picker-fields_row">
          <input class="range__picker-fields_date-input"
                 :disabled="disableFields"
                 @keyup.enter="($event) => { $event.target.blur() }"
                 @blur="changeInput($event, 'start')"
                 v-model="textData.start" />
          <span style="padding: 0 15px;"> - </span>
          <input class="range__picker-fields_date-input"
                 :disabled="disableFields"
                 @keyup.enter="($event) => { $event.target.blur() }"
                 @blur="changeInput($event, 'end')"
                 v-model="textData.end" />
        </div>
        <div class="range__picker-fields_row">
          <button class="range__picker-fields_cancel range__picker-fields_reset"
                  @click.prevent.stop="clear()">
            Reset
          </button>
          <button class="range__picker-fields_cancel"
                  @click.prevent.stop="reset(true, true)">
            Cancel
          </button>
          <button class="range__picker-fields_cancel range__picker-fields_apply"
                  @click.prevent.stop="apply">
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
// eslint-disable-next-line no-unused-vars
import BaseMultiSelectSortableHeader from './BaseMultiSelectSortableHeader'
/**
 * Date range picker component. used in {@link BaseMultiSelectSortableHeader}
 * used "v-calendar" library {@see https://vcalendar.io/datepicker.html}
 */

const DATE_RANGE_OPTIONS = [
  {
    label: 'Custom',
    value: 'custom'
  },
  {
    label: 'Last 3 Months',
    value: 'last_three_months'
  },
  {
    label: 'Last 12 Months',
    value: 'last_twelve_months'
  },
  {
    label: 'YTD',
    value: 'ytd'
  }
]
export default {
  name: 'BaseDateRangeFilterDropdown',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    propertyName: {
      type: String
    },
    modelValue: {
      default: false
    },
    useFields: {
      type: Boolean,
      default: false
    },
    columns: {
      default: 1
    },
    /* predefinedFilters allows to set earlier created filters */
    predefinedFilter: {
      type: String,
      default: null
    },
    minDate: {
      type: Object
    },
    maxDate: {
      type: Object
    },
    dates: {
      type: Array,
      default: () => []
    },
    fromPage: {
      type: Object
    }
  },
  data () {
    return {
      dragValue: {
        start: null,
        end: null
      },
      range: {
        start: null,
        end: null
      },
      appliedData: {
        start: null,
        end: null
      },
      textData: {
        start: '',
        end: ''
      },
      dateResult: null,
      isDataSelecting: false,
      dateRange: 'custom',
      appliedDateRange: 'custom',
      dateRangeOptions: _.cloneDeep(DATE_RANGE_OPTIONS)
    }
  },
  watch: {
    /**
     * set earlier created filters into component
     * */
    predefinedFilter: {
      handler (value) {
        if (value && value.start && value.end) {
          this.appliedData = {
            start: moment(value.start).isValid() ? moment(value.start).toDate() : null,
            end: moment(value.end).isValid() ? moment(value.end).toDate() : null
          }
          this.range = _.cloneDeep(this.appliedData)
          this.dragValue = _.cloneDeep(this.range)
          this.setDateResult()
          this.$emit('change', this.somethingIsApplied())
        }
      },
      immediate: true
    },
    range: {
      deep: true,
      handler () {
        try {
          const startDate = moment(this.range.start)
          this.textData.start = startDate.isValid() ? startDate.format('MMM D, YYYY') : ''
        } catch {
          this.textData.start = ''
        }
        try {
          const endDate = moment(this.range.end)
          this.textData.end = endDate.isValid() ? endDate.format('MMM D, YYYY') : ''
        } catch {
          this.textData.end = ''
        }
        if (!this.range || !this.range.start || !this.range.end) {
          return
        }
        this.setDateResult()
      }
    },
    dateRange (v) {
      if (v === 'ytd') {
        const date = new Date(new Date().getFullYear(), 0, 1)
        date.setHours(0, 0, 0, 0)
        const endDate = new Date()
        endDate.setHours(0, 0, 0, 0)
        this.range = {
          start: date,
          end: endDate
        }
      } else if (v === 'last_three_months') {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        date.setMonth(date.getMonth() - 3)
        const endDate = new Date()
        endDate.setHours(0, 0, 0, 0)
        this.range = {
          start: date,
          end: endDate
        }
      } else if (v === 'last_twelve_months') {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        date.setFullYear(date.getFullYear() - 1)
        const endDate = new Date()
        endDate.setHours(0, 0, 0, 0)
        this.range = {
          start: date,
          end: endDate
        }
      }
      if (v !== 'custom') {
        this.apply()
      }
    }
  },
  computed: {
    /**
     * drag attribute settings {@link https://vcalendar.io/datepicker.html#selection-popover}
     * */
    selectDragAttribute () {
      return {
        popover: {
          visibility: 'hover',
          isInteractive: false // Defaults to true when using slot
        }
      }
    },
    /**
     * select attribute settings {@link https://vcalendar.io/datepicker.html#selection-popover}
     * */
    attributes () {
      return [{
        key: 'snapshots',
        bar: true,
        dates: this.dates
      }]
    },
    disableFields () {
      return this.dateRange !== 'custom' || this.isDataSelecting
    },
    datePickerMinDate () {
      return this.minDate || null
    },
    datePickerMaxDate () {
      const endDate = new Date()
      endDate.setHours(0, 0, 0, 0)
      return this.useFields ? this.maxDate || endDate : null
    }
  },
  methods: {
    cancel () {
      this.clear()
    },
    clear (simpleClear) {
      this.range = { start: null, end: null }
      this.dragValue = { start: null, end: null }
      this.dateResult = null
      this.dateRange = 'custom'
      if (!simpleClear) {
        if (this.appliedData.start) {
          this.apply()
        } else {
          this.$emit('close')
        }
        this.$emit('change', this.somethingIsApplied())
      }
    },
    closeViaOverlay () {
      if (Object.values(this.range).some(v => !v)) {
        this.reset(true)
      }
    },
    handleDayClick (day) {
      this.dateRange = this.dateRangeOptions[0].value
      if (this.range.start && this.range.end) {
        this.range = {
          start: null,
          end: null
        }
      }
      if (!this.range) {
        this.range = {
          start: day.date,
          end: null
        }
        this.isDataSelecting = true
      } else if (!this.range.start) {
        this.range.start = day.date
        this.isDataSelecting = true
      } else {
        this.range.end = day.date
        this.isDataSelecting = false
      }

      if (this.range.start && this.range.end && this.range.start > this.range.end) {
        const end = this.range.start
        this.range.start = this.range.end
        this.range.end = end
      }
    },
    changeInput (value, item) {
      try {
        const date = moment(value.target.value)
        if (date.isValid()) {
          if (date._d < this.datePickerMaxDate) {
            this.range[item] = date._d
          } else {
            this.range[item] = this.datePickerMaxDate
          }
        }
        // uses for call recalculation logic for inputs
        this.range = _.cloneDeep(this.range)
      } catch {}
      if (this.range.start && this.range.end && this.range.start > this.range.end) {
        const end = this.range.start
        this.range.start = this.range.end
        this.range.end = end
      }
    },
    apply () {
      const range = {
        date_from: this.range.start ? moment(this.range.start).format('YYYY-MM-DD') : null,
        date_to: this.range.end ? moment(this.range.end).format('YYYY-MM-DD') : null
      }
      this.appliedDateRange = this.dateRange
      this.range && (this.appliedData = _.cloneDeep(this.range))
      this.$emit('apply', range)
    },
    /**
     * invoked from parent component when component is opened/closed
     * @param isOpened
     */
    reset  (isOpened, isApply) {
      if (isOpened) {
        this.range = _.cloneDeep(this.appliedData)
        this.dateRange = this.appliedDateRange
        this.$nextTick(this.removeClearButton)
      } else {
        const isSelected = this.somethingIsApplied()
        this.$emit('change', isSelected)
      }
      isApply && this.apply()
    },
    somethingIsApplied () {
      return this.appliedData ? (this.appliedData.start && this.appliedData.end) : false
    },
    setDateResult () {
      let result = ''
      const fromDate = moment(this.range.start)
      if (fromDate.isValid()) {
        result = fromDate.format('MMM DD')
        const toDate = moment(this.range.end)
        if (toDate.isValid()) {
          const to = toDate.format('MMM DD')
          if (to !== result) { result += ` - ${to}` }
        }
      }
      this.dateResult = result
    }
  }

}
</script>

<style lang="scss">
  .vc-reset {
    border-style: none !important;
  }

  .range {
    display: flex;
    flex-direction: column;
    padding-top: 5px;

    &__date-picker {
      display: flex;
    }

    &__picker-fields {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 9999;

      &_row {
        padding-top: 14px;
        display: flex;
        align-items: center;
      }

      &_cancel {
        padding: 7px 10px;
        border: none;
        outline: none;
        cursor: pointer;
        font-size: 10px;
        background: none;
        color: $rfx-dark-march;
        margin-left: auto;
      }

      &_apply {
        margin-left: 16px;
        background-color: $rfx-medium-march;
        color: white;
        border-radius: 4px;
      }

      &_reset {
        margin-right: auto;
        margin-left: 0;
      }

      &_date-input {
        padding: 7px 0 7px 5px;
        border-radius: 4px;
        border: 1px solid #e7e9eb;
        color: $rfx-dark-march;
        font-size: 14px;
        max-width: 90px;
      }
    }

    &__result {
      padding: 5px 15px;
      font-weight: normal;
      font-size: 13px;
    }

     &__separator{
      background-color: $rfx-bright-blue;
      height: 1px;
      margin: 10px 10px 0 15px;
    }

    &__result-container {
      position: relative;
      flex: 1 1 100%;
    }

    &__sort-container {
      display: flex;
      justify-content: center;
      padding: 5px 5px 0;
      width: 15px;
      align-self: flex-end;
    }

    &__clear-button {
      position: absolute;
      top: 3px;
      right: 8px;
      width: 16px;
      height: 16px;
      background-color: transparent;
      color: $rfx-dark-march;
      cursor: pointer;

      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 7px;
        top: 3px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }
</style>
